import * as React from "react";
import {
  Pressable,
  Text,
  ActivityIndicator,
  type PressableProps,
} from "react-native";
import { cva, type VariantProps } from "class-variance-authority";
import { useCSSVariable } from "uniwind";

import { cn } from "@/shared/lib/utils";

const buttonVariants = cva(
  "flex-row items-center justify-center gap-2 rounded-md active:opacity-90",
  {
    variants: {
      variant: {
        default: "bg-primary active:bg-primary/90",
        destructive: "bg-destructive active:bg-destructive/90",
        outline: "border-1 border-white bg-transparent active:bg-accent",
        secondary: "bg-secondary active:bg-secondary/80",
        ghost: "active:bg-accent",
        link: "",
      },
      size: {
        default: "h-12 px-4 py-3",
        sm: "h-10 rounded-md gap-1.5 px-3",
        lg: "h-14 rounded-md px-6",
        icon: "h-9 w-9",
        "icon-sm": "h-8 w-8",
        "icon-lg": "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const buttonTextVariants = cva("text-base font-semibold", {
  variants: {
    variant: {
      default: "text-primary-foreground",
      destructive: "text-white",
      outline: "text-foreground",
      secondary: "text-secondary-foreground",
      ghost: "text-foreground",
      link: "text-primary underline",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

type SpinnerColors = {
  foreground: string;
  primaryForeground: string;
  brand: string;
};

/**
 * Returns the appropriate spinner color for each button variant.
 * Colors match the text color for each variant to ensure visual consistency.
 */
function getSpinnerColor(variant: string, colors: SpinnerColors): string {
  switch (variant) {
    case "default":
      // primary-foreground - contrasts with primary background
      return colors.primaryForeground;
    case "destructive":
      // white text on pink destructive background
      return "#ffffff";
    case "outline":
    case "secondary":
    case "ghost":
      // foreground color for variants with transparent/light backgrounds
      return colors.foreground;
    case "link":
      // brand color for link text
      return colors.brand;
    default:
      return colors.foreground;
  }
}

export interface ButtonProps
  extends
    Omit<PressableProps, "children">,
    VariantProps<typeof buttonVariants> {
  children?: React.ReactNode;
  loading?: boolean;
  /** Web-only prop for form submission, ignored on native */
  type?: "button" | "submit" | "reset";
  accessibilityLabel?: string;
}

function Button({
  className,
  variant = "default",
  size = "default",
  disabled,
  loading,
  children,
  type: _type,
  accessibilityLabel,
  ...props
}: ButtonProps) {
  const [foreground, primaryForeground, brand] = useCSSVariable([
    "--color-foreground",
    "--color-primary-foreground",
    "--color-brand",
  ]) as [string, string, string];
  const isDisabled = disabled || loading;

  return (
    <Pressable
      className={cn(
        buttonVariants({ variant, size }),
        isDisabled && "opacity-50",
        className
      )}
      disabled={isDisabled}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      {...props}
    >
      {loading ? (
        <ActivityIndicator
          size="small"
          color={getSpinnerColor(variant ?? "default", {
            foreground,
            primaryForeground,
            brand,
          })}
        />
      ) : typeof children === "string" ? (
        <Text className={cn(buttonTextVariants({ variant }))}>{children}</Text>
      ) : (
        children
      )}
    </Pressable>
  );
}

export { Button, buttonVariants, buttonTextVariants };
